import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';

const STORAGE_KEY = 'ometis_cookies_consent';
const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

const hasSavedConsent = () => {
  try { return Boolean(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')); } catch { return false; }
};

const saveConsent = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, date: new Date().toISOString() }));
};

const useConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (hasSavedConsent()) return undefined;
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, [setVisible]);
  const decide = (data) => { saveConsent(data); setVisible(false); };
  return { visible, decide };
};

const Banner = ({ onPersonalize, onRefuse, onAccept }) => (
  <div className="fixed bottom-0 left-0 right-0 z-[60] bg-[#010c23] text-white border-t border-[#ff7300]/40 shadow-2xl" data-testid="cookie-banner">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6">
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
        <div className="flex items-start gap-4 flex-1">
          <span className="w-10 h-10 rounded-full bg-[#ff7300]/15 flex items-center justify-center flex-shrink-0 text-[#ff7300]"><Cookie size={20} /></span>
          <div>
            <p className="font-serif text-lg text-white">Vos préférences de cookies</p>
            <p className="text-sm text-white/70 mt-1 leading-relaxed">Nous utilisons des cookies pour améliorer votre expérience, mesurer l'audience et personnaliser les contenus. Vous pouvez accepter, refuser ou personnaliser à tout moment.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 self-stretch lg:self-auto">
          <button onClick={onPersonalize} className="text-sm px-4 py-2.5 border border-white/30 hover:border-white transition-colors" data-testid="cookie-personalize">Personnaliser</button>
          <button onClick={onRefuse} className="text-sm px-4 py-2.5 border border-white/30 hover:border-white transition-colors" data-testid="cookie-refuse">Tout refuser</button>
          <button onClick={onAccept} className="text-sm px-5 py-2.5 bg-[#ff7300] hover:bg-[#e56600] text-white transition-colors font-medium" data-testid="cookie-accept">Tout accepter</button>
        </div>
      </div>
    </div>
  </div>
);

const PrefRow = ({ title, description, checked, onChange, disabled }) => (
  <div className={`p-4 border ${checked ? 'border-[#ff7300]/60 bg-[#ff7300]/5' : 'border-black/10'} transition-colors`}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <p className="font-medium text-[#010c23]">{title}</p>
        <p className="text-xs text-[#010c23]/60 mt-1 leading-relaxed">{description}</p>
      </div>
      <label className={`relative inline-flex items-center ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}>
        <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange && onChange(e.target.checked)} className="sr-only peer" />
        <div className="w-11 h-6 bg-gray-300 peer-checked:bg-[#ff7300] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
      </label>
    </div>
  </div>
);

const PrefsModal = ({ onClose, onSave, onAccept }) => {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const setPref = (key) => (value) => setPrefs((p) => ({ ...p, [key]: value }));
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose} data-testid="cookie-prefs-modal">
      <div className="bg-white w-full md:max-w-lg md:rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#ff7300]">Cookies</p>
              <h3 className="font-serif text-2xl text-[#010c23] mt-2">Personnaliser vos préférences</h3>
            </div>
            <button onClick={onClose} className="text-[#010c23]/60 hover:text-[#010c23]" aria-label="Fermer" data-testid="cookie-prefs-close"><X size={20} /></button>
          </div>
          <div className="space-y-4">
            <PrefRow title="Cookies nécessaires" description="Indispensables au fonctionnement du site (navigation, sécurité). Toujours actifs." checked disabled />
            <PrefRow title="Mesure d'audience" description="Nous aident à comprendre comment le site est utilisé, de façon anonyme." checked={prefs.analytics} onChange={setPref('analytics')} />
            <PrefRow title="Personnalisation & marketing" description="Contenus adaptés à vos intérêts et communications personnalisées." checked={prefs.marketing} onChange={setPref('marketing')} />
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <button onClick={() => onSave(prefs)} className="btn-primary flex-1 justify-center" data-testid="cookie-prefs-save">Enregistrer mes choix</button>
            <button onClick={onAccept} className="btn-outline-dark flex-1 justify-center">Tout accepter</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CookieBanner = () => {
  const { visible, decide } = useConsentBanner();
  const [showPrefs, setShowPrefs] = useState(false);
  if (!visible) return null;

  const acceptAll = () => decide({ necessary: true, analytics: true, marketing: true, choice: 'accept_all' });
  const refuseAll = () => decide({ necessary: true, analytics: false, marketing: false, choice: 'refuse_all' });
  const savePrefs = (prefs) => decide({ ...prefs, choice: 'custom' });

  return (
    <>
      <Banner onPersonalize={() => setShowPrefs(true)} onRefuse={refuseAll} onAccept={acceptAll} />
      {showPrefs && <PrefsModal onClose={() => setShowPrefs(false)} onSave={savePrefs} onAccept={acceptAll} />}
    </>
  );
};

export default CookieBanner;
